import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { Directive, OnDestroy } from '@angular/core';
import { map, Observable, Subscription } from 'rxjs';

export const BreakpointList = {
  xs: Breakpoints.XSmall,
  sm: Breakpoints.Small,
  md: Breakpoints.Medium,
  lg: Breakpoints.Large,
  xl: `(min-width: 1920px) and (max-width: 2559px)`,
  xxl: `(min-width: 2560px) and (max-width: 4000px)`,
  mobile: Breakpoints.Handset,
  tablet: Breakpoints.Tablet,
  desktop: Breakpoints.Web,
  mp: Breakpoints.HandsetPortrait,
  tp: Breakpoints.TabletPortrait,
  dp: Breakpoints.WebPortrait,
  ml: Breakpoints.HandsetLandscape,
  tl: Breakpoints.TabletLandscape,
  dl: Breakpoints.WebLandscape,
} as const;

export type Breakpoint = (typeof BreakpointList)[keyof typeof BreakpointList];

/**
 * Usage example:
 *
 *  <ng-container breakpoint #ref="value">
 *    <div *ngIf="ref.isDesktop">göster</div>
 *    <div>{{ ref.isDesktop }}</div>
 *    <div>{{ ref.size }}</div>
 *  </ng-container>
 */

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[breakpoint]',
  exportAs: 'value',
  standalone: true,
})
export class BreakpointDirective implements OnDestroy {
  isMobile = false;
  isTablet = false;
  isDesktop = false;
  size: string | null = null;
  items: Record<string, Breakpoint> = {};
  private subscription = new Subscription();

  constructor(private breakpointObserver: BreakpointObserver) {
    this.items = BreakpointList;

    Object.keys(BreakpointList).forEach((key: any) => {
      this.subscription.add(
        this.breakpointObserver.observe([(BreakpointList as any)[key]]).subscribe((state) => {
          state.matches && (this.size = key);
        }),
      );
    });

    this.subscription.add(
      this.breakpointObserver.observe([Breakpoints.Handset]).subscribe((state) => {
        this.isMobile = state.matches;
      }),
    );

    this.subscription.add(
      this.breakpointObserver.observe([Breakpoints.Tablet]).subscribe((state) => {
        this.isTablet = state.matches;
      }),
    );

    this.subscription.add(
      this.breakpointObserver.observe([Breakpoints.Web]).subscribe((state) => {
        this.isDesktop = state.matches;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  isSmallerThan(breakpoint: string): Observable<boolean> {
    const breakpointIndexNumber: number = Object.keys(BreakpointList).indexOf(breakpoint);
    const query = this.breakpointObserver.observe(Object.values(BreakpointList));

    return query.pipe(
      map((state) => {
        const matchedIndex = Object.values(state.breakpoints).findIndex((value) => value);

        return breakpointIndexNumber > 0 ? matchedIndex < breakpointIndexNumber : true;
      }),
    );
  }

  isLargerThan(breakpoint: string): Observable<boolean> {
    const breakpointIndexNumber: number = Object.keys(BreakpointList).indexOf(breakpoint);
    const query = this.breakpointObserver.observe(Object.values(BreakpointList));

    return query.pipe(
      map((state) => {
        const matchedIndex = Object.values(state.breakpoints).findIndex((value) => value);

        return matchedIndex < 0 ? true : matchedIndex > breakpointIndexNumber;
      }),
    );
  }

  is(breakpoint: string): boolean {
    return window.matchMedia(this.items[breakpoint]).matches;
  }

  isLargerThanOld(breakpoint: string): Observable<boolean> {
    const query = this.breakpointObserver.observe([this.items[breakpoint]]);

    return query.pipe(map((state: BreakpointState) => !state.matches));
  }
}
